/* ═══════════════════════════════════════════════════════════════════════════
   AMSX report watermark & fingerprint (r83) \u2014 shared by every report / chart
   export (PPT, PDF, PNG) and by the admin "Check File" tool.

   Layers (see claude/REPORT_WATERMARK_DESIGN.md):
     1. VISIBLE   \u2014 footer band on every slide/page ("Prepared for <client> \u00b7
                    <user> \u00b7 <date> \u00b7 <Export ID>") + a light diagonal
                    "Licensed to <client>" across the plot area (survives
                    cropping a chart out of a slide).
     2. DOT-CODE  \u2014 a tiled lattice of near-white dots that encodes a 32-bit
                    code (export sequence or user id + CRC). Invisible, survives
                    screenshots / PDF re-export / PPT re-save / moderate JPEG.
     3. ZERO-WIDTH \u2014 the Export ID hidden in real text runs (U+200B / U+200C)
                    so copy-pasted text still carries it.
     4. LICENCE page \u2014 first slide/page with licensee, user, date, Export ID
                    and the disclosure line (deterrent).

   Everything here is plain ES5 and side-effect free (no DOM access except the
   canvas objects passed in), so it runs in the browser AND in Node tests.
   ═══════════════════════════════════════════════════════════════════════════ */

export var WM_VERSION = 1;
/* r84 (Noura 2026-10-12): "without these dots — just the text as watermark".
   The dot-code is DISABLED on every export; the visible text layers (footer band,
   diagonal licence text, licence page), the zero-width Export ID in real text and
   the document properties remain. The encoder/decoder stay in the module so the
   admin checker can still read older r83 files and the switch can be turned back
   on later (set WM_DOTS = true). */
export var WM_DOTS = false;

/* ── dot-code geometry (at the image's own pixel resolution) ── */
export var WM_PITCH = 16;          /* cell pitch in px                      */
export var WM_DOT = 6;             /* dot square side in px (centred)        */
export var WM_TILE = 8;            /* tile = 8 × 8 cells                     */
export var WM_SYNC = [1, 1, 1, 0, 1, 0, 0, 1];   /* row 0 of every tile       */
export var WM_INK = [246, 246, 240];   /* dot colour on white (multiply blend): warm light grey, luminance delta ~9 so it survives JPEG chroma subsampling (screenshots re-sent through chat apps) */

/* ── CRC-8 (poly 0x07) over the 24-bit payload ── */
export function wmCrc8(payload24) {
  var crc = 0;
  for (var i = 2; i >= 0; i--) {
    crc ^= (payload24 >>> (i * 8)) & 0xFF;
    for (var b = 0; b < 8; b++) crc = (crc & 0x80) ? (((crc << 1) ^ 0x07) & 0xFF) : ((crc << 1) & 0xFF);
  }
  return crc & 0xFF;
}

/* code32 = [kind:1][value:23][crc:8]; kind 0 = export sequence (ExportLog.ExportRecordId),
   kind 1 = user id (fallback when the export could not be registered). */
export function wmMakeCode(kind, value) {
  var v = (value >>> 0) & 0x7FFFFF;
  var payload = ((kind ? 1 : 0) << 23) | v;
  return ((payload << 8) | wmCrc8(payload)) >>> 0;
}
export function wmParseCode(code32) {
  var c = code32 >>> 0;
  var payload = (c >>> 8) & 0xFFFFFF, crc = c & 0xFF;
  if (wmCrc8(payload) !== crc) return null;
  return { kind: (payload >>> 23) & 1 ? "user" : "seq", value: payload & 0x7FFFFF };
}
export function wmBits(code32) {
  var bits = [];
  for (var i = 31; i >= 0; i--) bits.push((code32 >>> i) & 1);
  return bits;
}
/* cell (row r, col c) of a tile → 1/0 */
export function wmTileBit(bits, r, c) {
  if (r === 0) return WM_SYNC[c];
  if (r <= 4) return bits[(r - 1) * 8 + c];
  return bits[(r - 5) * 8 + c];              /* rows 5–7: bits 0..23 again */
}

/* ── identity for a stamp ──
   session: the api session object; reg: the /api/exports/register response
   (may be null when registration failed and the admin bypass applied). */
export function wmIdentity(session, reg, extra) {
  var u = (session && session.user) || {};
  var exportId = (reg && reg.exportId) || null;
  var seq = (reg && reg.seq != null) ? parseInt(reg.seq, 10) : null;
  var userId = parseInt(u.userId || u.id || 0, 10) || 0;
  var code = (seq != null && seq > 0 && seq < 0x800000) ? wmMakeCode(0, seq) : wmMakeCode(1, userId);
  var d = new Date();
  var pad = function (n) { return n < 10 ? "0" + n : String(n); };
  var MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return {
    exportId: exportId,
    seq: seq,
    userId: userId,
    user: u.name || u.fullName || u.email || ("user #" + userId),
    email: u.email || u.username || "",
    client: u.clientName || "",
    date: pad(d.getDate()) + " " + MON[d.getMonth()] + " " + d.getFullYear() + " " + pad(d.getHours()) + ":" + pad(d.getMinutes()),
    code: code,
    title: (extra && extra.title) || "",
    /* r97: end customer of a reseller partner (IQVIA) \u2014 the licence page names the distributor */
    partner: (u.partner && u.partner.name) ? String(u.partner.name) : "",
    partnerTerms: (u.partner && u.partner.terms) ? String(u.partner.terms) : "",
    partnerLogo: (u.partner && u.partner.logo) ? String(u.partner.logo) : "",   /* r130 */
    unregistered: !exportId
  };
}

/* ── ZERO-WIDTH text carrier ──
   U+2062 start \u00b7 U+200B = 0 \u00b7 U+200C = 1 \u00b7 U+2063 end. Encodes the Export ID
   hex (after "AMSX-") as 4 bits per hex digit. */
export function wmZeroWidth(exportId) {
  if (!exportId) return "";
  var hex = String(exportId).replace(/^AMSX-/i, "").toUpperCase();
  var out = "\u2062";
  for (var i = 0; i < hex.length; i++) {
    var n = parseInt(hex.charAt(i), 16); if (isNaN(n)) continue;
    for (var b = 3; b >= 0; b--) out += ((n >> b) & 1) ? "\u200C" : "\u200B";
  }
  return out + "\u2063";
}
export function wmDecodeZeroWidth(text) {
  var m = new RegExp("\\u2062([\\u200B\\u200C]+)\\u2063").exec(String(text || ""));   /* built from escapes: charset-proof */
  if (!m) return null;
  var s = m[1], hex = "";
  for (var i = 0; i + 4 <= s.length; i += 4) {
    var n = 0;
    for (var b = 0; b < 4; b++) n = (n << 1) | (s.charAt(i + b) === "\u200C" ? 1 : 0);
    hex += n.toString(16).toUpperCase();
  }
  return hex ? "AMSX-" + hex : null;
}
/* weave the carrier into a visible string (after the first word) */
export function wmWeave(text, exportId) {
  var zw = wmZeroWidth(exportId);
  if (!zw) return text;
  var s = String(text || "");
  var i = s.indexOf(" ");
  return i > 0 ? s.slice(0, i) + zw + s.slice(i) : s + zw;
}

/* ── DOT-CODE: stamp a canvas in place ── */
export function wmStampDots(cv, code32) {
  var ctx = cv.getContext("2d");
  var bits = wmBits(code32);
  var w = cv.width, h = cv.height, P = WM_PITCH, D = WM_DOT, off = (P - D) / 2;
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = "rgb(" + WM_INK[0] + "," + WM_INK[1] + "," + WM_INK[2] + ")";
  var rows = Math.ceil(h / P), cols = Math.ceil(w / P);
  for (var j = 0; j < rows; j++) {
    var r = j % WM_TILE;
    for (var i = 0; i < cols; i++) {
      if (wmTileBit(bits, r, i % WM_TILE)) ctx.fillRect(i * P + off, j * P + off, D, D);
    }
  }
  ctx.restore();
}

/* ── VISIBLE layers: footer band + diagonal licence text.
   Returns a NEW canvas (height = h + band). opts: { diagonal:true, band:true } */
/* r150: the footer band's height for a capture of width w (0 when the band is off) — exported so
   the export frame can reserve it and the FINAL page (capture + band) is exactly 16:9 */
export function wmBandHeight(w, opts) {
  if (opts && opts.band === false) return 0;
  var fs = Math.max(13, Math.min(26, Math.round(w / 105)));
  return Math.round(fs * 2.1);
}
export function wmStampCanvas(src, id, opts) {
  opts = opts || {};
  var w = src.width, h = src.height;
  var fs = Math.max(13, Math.min(26, Math.round(w / 105)));       /* footer font px */
  var band = wmBandHeight(w, opts);
  var out = document.createElement("canvas");
  out.width = w; out.height = h + band;
  var ctx = out.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h + band);
  ctx.drawImage(src, 0, 0);
  /* diagonal "Licensed to …" across the plot area */
  if (opts.diagonal !== false) {
    /* r140 (Noura): the diagonal mark is just "<client> - AMSX" — the user, e-mail and
       date are in every slide's footer band already */
    var txt = (id.client || id.user) + " - AMSX";
    var dfs = Math.max(18, Math.round(w / 22));
    /* r152 (Noura's navy divider slides: the navy-on-navy mark came out as dark smudges after JPEG):
       the mark follows the page — navy on light pages, white on dark (cover / divider) pages */
    var dlum = 255;
    try { var dpx = ctx.getImageData(Math.round(w * 0.1), Math.round(h * 0.5), Math.round(w * 0.8), 1).data, dsum = 0, dcnt = 0; for (var dq = 0; dq < dpx.length; dq += 4 * 16) { dsum += 0.299 * dpx[dq] + 0.587 * dpx[dq + 1] + 0.114 * dpx[dq + 2]; dcnt++; } dlum = dcnt ? dsum / dcnt : 255; } catch (eDL) {}
    ctx.save();
    ctx.globalAlpha = dlum < 120 ? 0.06 : 0.04;
    ctx.fillStyle = dlum < 120 ? "#ffffff" : "#1A3F6E";
    ctx.font = "bold " + dfs + "px Arial, Helvetica, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.translate(w / 2, h / 2); ctx.rotate(-Math.PI / 7);
    var stepX = Math.max(ctx.measureText(txt).width * 1.25, w / 2.4), stepY = Math.max(dfs * 6, h / 3.2);
    for (var yy = -h; yy <= h; yy += stepY) {
      var shift = (Math.round(yy / stepY) % 2) ? stepX / 2 : 0;
      for (var xx = -w; xx <= w; xx += stepX) ctx.fillText(txt, xx + shift, yy);
    }
    ctx.restore();
  }
  /* r86: SAMPLE stamp — one big diagonal word across the page (sample reports) */
  if (opts.sample) {
    /* navy on light pages, white on dark (divider / cover) pages */
    var lum = 255;
    try { var px = ctx.getImageData(Math.round(w * 0.1), Math.round(h * 0.5), Math.round(w * 0.8), 1).data, sum = 0, cnt = 0; for (var q = 0; q < px.length; q += 4 * 16) { sum += 0.299 * px[q] + 0.587 * px[q + 1] + 0.114 * px[q + 2]; cnt++; } lum = cnt ? sum / cnt : 255; } catch (eL) {}
    ctx.save();
    ctx.globalAlpha = lum < 120 ? 0.14 : 0.085; ctx.fillStyle = lum < 120 ? "#ffffff" : "#005487";
    /* r87: the label is "SAMPLE · <client>" — shrink until it spans the page diagonal */
    var sfs = w / 5.2, sTxt = String(opts.sample).toUpperCase();
    ctx.font = "bold " + Math.round(sfs) + "px Arial, Helvetica, sans-serif";
    while (ctx.measureText(sTxt).width > w * 1.02 && sfs > w / 16) { sfs *= 0.92; ctx.font = "bold " + Math.round(sfs) + "px Arial, Helvetica, sans-serif"; }
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.translate(w / 2, h / 2); ctx.rotate(-Math.PI / 8);
    ctx.fillText(sTxt, 0, 0);
    ctx.restore();
  }
  /* footer band */
  if (band > 0) {
    ctx.fillStyle = opts.sample ? "#e6f1f8" : "#f1f5f9"; ctx.fillRect(0, h, w, band);
    ctx.fillStyle = "#dbe3ee"; ctx.fillRect(0, h, w, Math.max(1, Math.round(fs / 10)));
    var pad = Math.round(fs * 1.1), cy = h + band / 2;
    ctx.textBaseline = "middle";
    ctx.font = "bold " + fs + "px Arial, Helvetica, sans-serif";
    ctx.fillStyle = "#1A3F6E"; ctx.textAlign = "left";
    var left1 = (opts.sample ? "SAMPLE REPORT  \u00b7  " : "") + "Prepared for " + (id.client || id.user);
    ctx.fillText(left1, pad, cy);
    var x2 = pad + ctx.measureText(left1).width;
    ctx.font = fs + "px Arial, Helvetica, sans-serif"; ctx.fillStyle = "#475569";
    var left2 = "  \u00b7  " + (id.email || id.user) + "  \u00b7  " + id.date;
    ctx.fillText(left2, x2, cy);
    ctx.textAlign = "right";
    /* r85: the Export ID is never shown — it travels only in the hidden carriers */
    /* r130: reseller partner → "<Partner> · powered by AMS Analytix" leads the right-hand text */
    var brand9 = id.partner ? ("Distributed by " + id.partner + "  \u00b7  under licence from AMS Analytix") : "AMS Analytix";   /* r134 */
    var right = brand9 + "  \u00b7  individually watermarked & traceable  \u00b7  amsanalytix.com";
    if (ctx.measureText(right).width + x2 + ctx.measureText(left2).width + pad * 3 > w) right = brand9 + "  \u00b7  amsanalytix.com";
    ctx.fillStyle = "#64748b"; ctx.fillText(right, w - pad, cy);
  }
  if (WM_DOTS && opts.dots !== false) wmStampDots(out, id.code);
  return out;
}

/* ── LICENCE page canvas (w × h) — CLOSING slide in the report theme (r85):
   navy page, teal strip + accent bar, eyebrow, white title, muted labels. The
   Export ID is NOT printed (hidden carriers only). ── */
export var WM_ORDER_EMAIL = "info@amsanalytix.com";
export var WM_ORDER_URL = "https://app.amsanalytix.com:8083/";
export var WM_CTA_BOX = { x: 0.0475, y: 0.80, w: 0.44, h: 0.065 };   /* licence-page CTA box, page fractions (r153: at the divider left margin, above the footer line) */
export var WM_THEME = { navy: "#005487", teal: "#49B8D3", tealL: "#6FC4E3", pale: "#CFE3F1", muted: "#8FB9D2", white: "#FFFFFF" };
export function wmLicenceCanvas(w, h, id, opts) {
  /* r153 (Noura: "update this slide — issue in logo, size and fill; make it the same setup as the
     other slides"): the closing page is laid out like the deck's navy divider slides — full navy,
     the real AMS logo (white) top-left at the divider's fixed position and size, content at the
     divider's left margin, no teal strip / dot motif — and it carries the SAME footer band as every
     other exported page (opts.band, default on; off for a clean export, which then gets a small
     in-page footer line). w × h is the FINAL page size (band included). */
  opts = opts || {};
  var T = WM_THEME;
  var band = opts.band === false ? 0 : wmBandHeight(w);
  var hh = h - band;                                  /* the page body above the band */
  var cv = document.createElement("canvas"); cv.width = w; cv.height = hh;
  var ctx = cv.getContext("2d");
  ctx.fillStyle = T.navy; ctx.fillRect(0, 0, w, hh);
  var u = Math.min(w / 1220, hh / 668);               /* design unit = the 1220 × 668 slide body */
  ctx.textBaseline = "alphabetic";
  var text = function (str, x, y, size, color, bold, align, spacing) {
    ctx.font = (bold ? "bold " : "") + Math.round(size * u) + "px Arial, Helvetica, sans-serif";
    ctx.fillStyle = color; ctx.textAlign = align || "left";
    if (spacing) { try { ctx.letterSpacing = spacing + "px"; } catch (e) {} }
    ctx.fillText(str, x, y);
    if (spacing) { try { ctx.letterSpacing = "0px"; } catch (e) {} }
  };
  var wrap = function (str, x, y, size, color, maxW, lh) {
    ctx.font = Math.round(size * u) + "px Arial, Helvetica, sans-serif"; ctx.fillStyle = color; ctx.textAlign = "left";
    var words = String(str).split(" "), line = "", yy = y;
    for (var i = 0; i < words.length; i++) {
      var t = line + (line ? " " : "") + words[i];
      if (ctx.measureText(t).width > maxW && line) { ctx.fillText(line, x, yy); line = words[i]; yy += lh * u; } else line = t;
    }
    if (line) ctx.fillText(line, x, yy);
    return yy + lh * u;
  };
  var L = 58 * u;                                     /* the divider slides' left padding */
  /* ── brand mark, the divider slides' fixed template: AMS logo 52 px high at (52, 34); with a
     reseller partner the r140 lockup — partner logo 24 px (its own white version, else a white
     chip), hairline, AMS logo 30 px, 10 px gaps — left-anchored at the same spot ── */
  var lx = 52 * u, ly = 34 * u;
  var ams = (opts.amsImg && opts.amsImg.naturalWidth > 0) ? opts.amsImg : null;
  var drawAms = function (x, y, hgt) {
    if (ams) { var ww = hgt * ams.naturalWidth / Math.max(1, ams.naturalHeight); try { ctx.drawImage(ams, x, y, ww, hgt); } catch (e) {} return ww; }
    /* fallback wordmark when the logo file could not be loaded */
    var base = y + hgt * 0.78;
    text("AMS", x, base, hgt * 0.62, T.white, true);
    ctx.font = "bold " + Math.round(hgt * 0.62) + "px Arial, Helvetica, sans-serif"; var aw = ctx.measureText("AMS ").width;
    text("Analytix", x + aw, base, hgt * 0.62, T.tealL, true);
    return aw + ctx.measureText("Analytix").width;
  };
  if (opts.partnerImg && opts.partnerImg.naturalWidth > 0) {
    var ph9 = 24 * u, pw9 = ph9 * opts.partnerImg.naturalWidth / Math.max(1, opts.partnerImg.naturalHeight);
    if (pw9 > 0.22 * w) { pw9 = 0.22 * w; ph9 = pw9 * opts.partnerImg.naturalHeight / Math.max(1, opts.partnerImg.naturalWidth); }
    var rowH = 30 * u, cy9 = ly + (rowH - ph9) / 2, padX = 6 * u, padY = 4 * u;
    if (opts.partnerImg._amsDark9) { padX = 0; padY = 0; }                    /* r131: the partner's own white logo → no chip */
    else { ctx.fillStyle = T.white; try { ctx.beginPath(); ctx.roundRect(lx, cy9 - padY, pw9 + padX * 2, ph9 + padY * 2, 4 * u); ctx.fill(); } catch (e) { ctx.fillRect(lx, cy9 - padY, pw9 + padX * 2, ph9 + padY * 2); } }
    try { ctx.drawImage(opts.partnerImg, lx + padX, cy9, pw9, ph9); } catch (e2) {}
    var wx9 = lx + pw9 + padX * 2 + 10 * u;
    ctx.fillStyle = "rgba(255,255,255,.3)"; ctx.fillRect(wx9, ly + 2 * u, Math.max(1, Math.round(1 * u)), rowH - 4 * u);   /* r137: two marks, a hairline, no wording */
    drawAms(wx9 + 10 * u, ly, rowH);
  } else {
    drawAms(lx, ly, 52 * u);
  }
  /* ── eyebrow · title · rows · terms, the divider's type scale ── */
  var y = 150 * u;
  text("LICENCE & TRACEABILITY", L, y, 13, T.teal, true, "left", 3.5 * u);
  y += 56 * u;
  var tfs = 40; ctx.font = "bold " + Math.round(tfs * u) + "px Arial, Helvetica, sans-serif";
  var tw = w - 2 * L;
  while (tfs > 22 && ctx.measureText(id.title || "").width > tw) { tfs -= 2; ctx.font = "bold " + Math.round(tfs * u) + "px Arial, Helvetica, sans-serif"; }
  text(id.title || "Market intelligence report", L, y, tfs, T.white, true);
  y += 58 * u;
  var rows = [
    ["Licensed to", id.client || id.user || "\u2014"],
    ["Prepared for", id.user + (id.email ? "  (" + id.email + ")" : "")],
    ["Generated", id.date]
  ];
  if (id.partner) rows.splice(1, 0, ["Distributed by", id.partner + "  \u00b7  under licence from AMS Analytix"]);   /* r97 */
  var step = (id.partner ? 32 : 36) * u;
  rows.forEach(function (r) {
    ctx.fillStyle = T.teal; ctx.fillRect(L, y - 13 * u, 3 * u, 17 * u);                 /* the divider rows' teal accent bar */
    text(r[0].toUpperCase(), L + 14 * u, y, 10.5, T.muted, true, "left", 2 * u);
    text(r[1], L + 14 * u + 190 * u, y, 15, T.white, true);
    y += step;
  });
  var ty = y + 16 * u, tfs9 = opts.sample ? 11.5 : 12.5, tlh9 = opts.sample ? 17 : 19;
  ty = wrap("This document and the data it contains are the property of AMS Analytix and are licensed to the client named above for its internal use only. Reproduction, redistribution, resale or disclosure to any third party, in whole or in part, including screenshots and extracts of charts or tables, is not permitted without the written consent of AMS Analytix.", L, ty, tfs9, T.pale, tw, tlh9);
  ty += 6 * u;
  wrap((id.partnerTerms ? id.partnerTerms + " " : "") + "Every copy of this report is individually watermarked and traceable to the licensee and to the user who generated it.", L, ty, tfs9, T.tealL, tw, tlh9);
  /* the divider slides' in-page footer line (bottom 24 px): report line left, site right — on a
     clean export (no band) the right side carries the Prepared-for line instead */
  ctx.globalAlpha = 0.8;
  text((id.client || id.user) + "  \u00b7  " + (id.title || "AMS Analytix report") + "  \u00b7  " + (id.partner ? id.partner + " \u00b7 under licence from AMS Analytix" : "AMS-Analytix"), L, hh - 24 * u, 10.5, T.muted, false, "left");
  text(band ? "amsanalytix.com" : ("Prepared for " + (id.client || id.user) + "  \u00b7  " + (id.email || id.user) + "  \u00b7  " + id.date), w - L, hh - 24 * u, 10.5, T.muted, false, "right");
  ctx.globalAlpha = 1;
  if (opts.sample) {
    /* r87: "ORDER THE FULL REPORT" call-to-action (the PPT / PDF exporters lay a
       hyperlink over this box — WM_CTA_BOX gives its geometry as fractions of the FULL page) */
    var bx = WM_CTA_BOX.x * w, by = WM_CTA_BOX.y * h, bw = WM_CTA_BOX.w * w, bh = WM_CTA_BOX.h * h;
    if (by + bh > hh - 4 * u) by = hh - 4 * u - bh;
    ctx.fillStyle = T.teal; ctx.fillRect(bx, by, bw, bh);
    text("ORDER THE FULL REPORT  \u2192  " + WM_ORDER_EMAIL, bx + bw / 2, by + bh * 0.66, 12, T.navy, true, "center");
  }
  var out9 = wmStampCanvas(cv, id, { diagonal: false, band: band > 0, dots: WM_DOTS, sample: opts.sample || null });
  return out9;
}

/* ── footer strings for real-text carriers (PPT text box / PDF invisible line) ── */
export function wmFooterText(id) {
  /* r85: no plain Export ID in the carrier either — the ID is the zero-width run only */
  return wmWeave("Prepared for " + (id.client || id.user) + " \u00b7 " + (id.email || id.user) + " \u00b7 " + id.date + " \u00b7 AMS Analytix \u2014 individually watermarked & traceable", id.exportId);
}
export function wmSubject(id) {
  return "AMSX Export ID: " + (id.exportId || "unregistered") + " | Exported by: " + (id.email || id.user) + (id.client ? " | Licensed to: " + id.client : "");
}

/* ═══════════════════════════════════════════════════════════════════════════
   DECODER \u2014 reads the dot-code back from any ImageData-like {width,height,data}
   (RGBA). Scale, crop and phase are unknown: pitch is recovered from the
   autocorrelation of the dot projections, phase from lattice sums, the tile
   origin from the sync row, and every bit by majority vote over all tiles.
   Returns { ok, code, kind, value, pitch, confidence, tiles, reason }.
   ═══════════════════════════════════════════════════════════════════════════ */
export function wmDecodeDots(img, opts) {
  opts = opts || {};
  var w = img.width, h = img.height, d = img.data;
  if (w < 60 || h < 60) return { ok: false, reason: "image too small" };
  var n = w * h, L = new Float32Array(n), i, x, y;
  /* luminance (JPEG keeps luma far better than chroma) */
  for (i = 0; i < n; i++) L[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
  /* local background = sliding max (radius R) \u2014 separable */
  var R = opts.radius || 8;
  var M1 = new Float32Array(n), BG = new Float32Array(n);
  for (y = 0; y < h; y++) {
    var row = y * w;
    for (x = 0; x < w; x++) {
      var mx = 0, x0 = Math.max(0, x - R), x1 = Math.min(w - 1, x + R);
      for (var k = x0; k <= x1; k++) if (L[row + k] > mx) mx = L[row + k];
      M1[row + x] = mx;
    }
  }
  for (x = 0; x < w; x++) {
    for (y = 0; y < h; y++) {
      var my = 0, y0 = Math.max(0, y - R), y1 = Math.min(h - 1, y + R);
      for (var kk = y0; kk <= y1; kk++) if (M1[kk * w + x] > my) my = M1[kk * w + x];
      BG[y * w + x] = my;
    }
  }
  /* dot darkness, only in light regions AND away from real content: pixels within
     6 px of anything dark (text, bars, edges) are excluded, so only the isolated
     dots on flat background feed the lattice search */
  var D = new Float32Array(n), total = 0, CM = new Uint8Array(n);
  for (i = 0; i < n; i++) { var bg = BG[i]; var v = (bg >= 215) ? (bg - L[i]) : 99; if (v > 24) CM[i] = 1; }
  var RC = 6, CM1 = new Uint8Array(n);
  for (y = 0; y < h; y++) { var rw = y * w; for (x = 0; x < w; x++) { var hit = 0, xa = Math.max(0, x - RC), xb = Math.min(w - 1, x + RC); for (var k2 = xa; k2 <= xb && !hit; k2++) hit = CM[rw + k2]; CM1[rw + x] = hit; } }
  for (x = 0; x < w; x++) { for (y = 0; y < h; y++) { var hit2 = 0, ya = Math.max(0, y - RC), yb = Math.min(h - 1, y + RC); for (var k3 = ya; k3 <= yb && !hit2; k3++) hit2 = CM1[k3 * w + x]; if (!hit2) { var bg2 = BG[y * w + x]; var v2 = bg2 - L[y * w + x]; if (v2 < 0) v2 = 0; D[y * w + x] = v2; total += v2; } } }
  if (total <= 0) return { ok: false, reason: "no light-area signal" };
  /* HIGH-PASS: the light diagonal licence text (and any faint broad shading) is as
     light as the dots but much wider — subtract a local box mean so only small
     blobs remain. The box radius must sit between the dot size and the stroke
     width, which depend on the unknown scale: try 3 radii, keep the first that
     yields a lattice. */
  var boxHP = function (r) {
    var out = new Float32Array(n), tmp = new Float32Array(n), xx9, yy9;
    for (yy9 = 0; yy9 < h; yy9++) { var rw9 = yy9 * w, run = 0; for (xx9 = 0; xx9 < w; xx9++) { run += D[rw9 + Math.min(w - 1, xx9 + r)]; if (xx9 - r - 1 >= 0) run -= D[rw9 + xx9 - r - 1]; else if (xx9 === 0) { run = 0; for (var q9 = 0; q9 <= Math.min(w - 1, r); q9++) run += D[rw9 + q9]; } tmp[rw9 + xx9] = run / (Math.min(w - 1, xx9 + r) - Math.max(0, xx9 - r) + 1); } }
    for (xx9 = 0; xx9 < w; xx9++) { var run2 = 0; for (yy9 = 0; yy9 < h; yy9++) { run2 += tmp[Math.min(h - 1, yy9 + r) * w + xx9]; if (yy9 - r - 1 >= 0) run2 -= tmp[(yy9 - r - 1) * w + xx9]; else if (yy9 === 0) { run2 = 0; for (var q8 = 0; q8 <= Math.min(h - 1, r); q8++) run2 += tmp[q8 * w + xx9]; } var m9 = run2 / (Math.min(h - 1, yy9 + r) - Math.max(0, yy9 - r) + 1); var v9 = D[yy9 * w + xx9] - m9; out[yy9 * w + xx9] = v9 > 0 ? v9 : 0; } }
    return out;
  };
  var Px = new Float32Array(w), Py = new Float32Array(h), DH = D, px = 0, py = 0, hpR = 0;
  /* pitch: every multiple of the pitch is an autocorrelation peak (the data bits
     make their heights vary), so take the SMALLEST prominent peak, not the highest */
  var _acDbg = {};
  var estPitch = function (P, tag) {
    /* detrend: the broad darkness of text / chart edges decays slowly in the
       autocorrelation and buries the lattice peaks — subtract a 64-px moving mean */
    var len = P.length, a, win = 64, Q = new Float32Array(len), run = 0, cnt = 0;
    for (a = 0; a < len; a++) { run += P[a]; cnt++; if (a >= win) { run -= P[a - win]; cnt--; } Q[a] = P[a]; }
    run = 0; cnt = 0;
    for (a = 0; a < len; a++) { var hi = Math.min(len - 1, a + win / 2), lo = a - win / 2 - 1; run += P[hi]; cnt++; if (lo >= 0) { run -= P[lo]; cnt--; } Q[a] = P[a] - run / cnt; }
    var MAXL = 48, vals = new Float32Array(MAXL + 2), best = 0;
    for (var lag = 2; lag <= MAXL; lag++) { var sum = 0; for (a = 0; a + lag < len; a++) sum += Q[a] * Q[a + lag]; vals[lag] = sum / (len - lag); if (vals[lag] > best) best = vals[lag]; }
    if (opts.debug) _acDbg[tag] = Array.prototype.slice.call(vals).map(function (v) { return Math.round(v / (best || 1) * 100) / 100; });
    if (best <= 0) return 0;
    var lagP = 0;
    for (lag = 3; lag <= MAXL - 1 && !lagP; lag++) {
      if (!(vals[lag] >= 0.35 * best && vals[lag] > vals[lag - 1] && vals[lag] >= vals[lag + 1])) continue;
      /* a true lattice period is echoed at EVERY multiple (heights vary with the
         data bits but never go clearly negative); a sub-multiple of the pitch is
         anticorrelated at its odd multiples */
      var okM = true, sumM = vals[lag], nM = 1;
      var aP = vals[lag - 1] || 0, bP = vals[lag], cP = vals[lag + 1] || 0, dP = (aP - 2 * bP + cP);
      var pr = lag + (dP !== 0 ? 0.5 * (aP - cP) / dP : 0);        /* fractional pitch */
      for (var mult = 2; Math.round(mult * pr) + 2 <= MAXL; mult++) {
        var ctr = Math.round(mult * pr), tol = Math.max(1, Math.round(mult * 0.08)), mv = -Infinity;
        for (var t9 = ctr - tol; t9 <= ctr + tol; t9++) if (t9 >= 2 && t9 <= MAXL && vals[t9] > mv) mv = vals[t9];
        if (mv < -0.05 * best) { okM = false; break; }
        sumM += mv; nM++;
      }
      if (!okM) continue;
      if (nM === 1 && vals[lag] < 0.5 * best) continue;
      if (sumM / nM >= 0.3 * best) lagP = lag;
    }
    if (!lagP) return 0;
    var a1 = vals[lagP - 1] || 0, b1 = vals[lagP], c1 = vals[lagP + 1] || 0, den = (a1 - 2 * b1 + c1);
    return lagP + (den !== 0 ? 0.5 * (a1 - c1) / den : 0);
  };
  var RADII = [4, 7, 11];
  for (var ri = 0; ri < RADII.length && !(px && py); ri++) {
    hpR = RADII[ri]; DH = boxHP(hpR);
    for (i = 0; i < w; i++) Px[i] = 0; for (i = 0; i < h; i++) Py[i] = 0;
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) { var vv = DH[y * w + x]; Px[x] += vv; Py[y] += vv; }
    px = estPitch(Px, "x" + hpR); py = estPitch(Py, "y" + hpR);
    if (px && !py) py = px; else if (py && !px) px = py;      /* the lattice is square */
    if (px && py && Math.abs(px - py) > 0.12 * px) { px = 0; py = 0; }
  }
  if (!px || !py || Math.abs(px - py) > 0.12 * px) return { ok: false, reason: "no lattice (px=" + (px || 0).toFixed(2) + ", py=" + (py || 0).toFixed(2) + ")", debug: opts.debug ? _acDbg : undefined };
  var p = (px + py) / 2;
  /* everything below reads the code for a given pitch; when the autocorrelation
     locked on a multiple of the pitch (harsh JPEG) the CRC fails and we retry at p/2 */
  var readAt = function (p) {
    /* phase */
    var phase = function (P, pitch) { var best = -1, bestF = 0, len = P.length; for (var f = 0; f < pitch; f += 0.25) { var s = 0, c = 0; for (var q = f; q < len - 1; q += pitch) { var fl = Math.floor(q), fr = q - fl; s += P[fl] * (1 - fr) + P[fl + 1] * fr; c++; } s /= c || 1; if (s > best) { best = s; bestF = f; } } return bestF; };
    /* lattice positions with LOCAL phase tracking: the phase is measured in sliding
       windows and the lattice is re-snapped window by window, so a 0.5 % pitch error
       or JPEG block bias can no longer drift the grid off the dots across a slide */
    var phaseIn = function (P, pitch, a0, a1) { var best = -1, bestF = 0; for (var f = 0; f < pitch; f += 0.25) { var s = 0, c = 0; for (var q = a0 + f; q < a1 - 1; q += pitch) { var fl = Math.floor(q), fr = q - fl; s += P[fl] * (1 - fr) + P[fl + 1] * fr; c++; } s /= c || 1; if (s > best) { best = s; bestF = f; } } return a0 + bestF; };
    var positions = function (P, pitch) {
      var len = P.length, W = Math.max(Math.round(pitch * 28), 160), step = Math.round(W / 2), out = [];
      if (W >= len) W = len;
      var anchors = [];
      for (var a0 = 0; a0 + W <= len || a0 === 0; a0 += step) { var a1 = Math.min(len, a0 + W); anchors.push({ c: (a0 + a1) / 2, ph: phaseIn(P, pitch, a0, a1) }); if (a1 >= len) break; }
      var x = anchors[0].ph; while (x - pitch >= 0) x -= pitch;
      var k = 0;
      while (x < len - 1) {
        while (k + 1 < anchors.length && x > anchors[k + 1].c) {
          k++;
          var ph = anchors[k].ph, dlt = ((x - ph) % pitch + pitch) % pitch; if (dlt > pitch / 2) dlt -= pitch;
          x -= dlt;                                     /* snap to the local lattice */
        }
        out.push(x); x += pitch;
      }
      return out;
    };
    /* global energy refinement first (the autocorrelation peak is biased by JPEG's
       8-px block grid), then the local tracking above */
    var energy = function (P, pitch) { var best = -1, len = P.length; for (var f = 0; f < pitch; f += 0.25) { var s = 0, c = 0; for (var q = f; q < len - 1; q += pitch) { var fl = Math.floor(q), fr = q - fl; s += P[fl] * (1 - fr) + P[fl + 1] * fr; c++; } s /= c || 1; if (s > best) best = s; } return best; };
    var refine = function (P, p0) { var bestP = p0, bestE = -1; for (var pp = p0 * 0.93; pp <= p0 * 1.07; pp += p0 * 0.001) { var e = energy(P, pp); if (e > bestE) { bestE = e; bestP = pp; } } return bestP; };
    var pxr = refine(Px, p), pyr = refine(Py, p);
    var xs = positions(Px, pxr), ys = positions(Py, pyr);
    var cols = xs.length, rows = ys.length, pX = pxr, pY = pyr;
    /* cell scores (mean darkness in the dot core) */
    if (cols < 10 || rows < 10) return { ok: false, reason: "too few cells" };
    var rad = Math.max(1, p * 0.17);
    var S = new Float32Array(cols * rows);
    for (var j = 0; j < rows; j++) for (i = 0; i < cols; i++) {
      var cx = xs[i], cy = ys[j], s = 0, c = 0;
      for (var yy = Math.round(cy - rad); yy <= Math.round(cy + rad); yy++) for (var xx = Math.round(cx - rad); xx <= Math.round(cx + rad); xx++) {
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue; s += DH[yy * w + xx]; c++;
      }
      S[j * cols + i] = c ? s / c : 0;
    }
    /* class averages with origin (0,0), then the tile origin = the cyclic shift that
       best matches BOTH the sync row and the duplicate rows (5–7 repeat 1–3) \u2014 the
       sync row alone can collide with a data row */
    var T = WM_TILE, A0 = new Float32Array(T * T), C0 = new Int32Array(T * T);
    for (j = 0; j < rows; j++) for (i = 0; i < cols; i++) { A0[(j % T) * T + (i % T)] += S[j * cols + i]; C0[(j % T) * T + (i % T)]++; }
    var mAll = 0; for (i = 0; i < T * T; i++) { A0[i] = C0[i] ? A0[i] / C0[i] : 0; mAll += A0[i]; } mAll /= T * T;
    var sd = 0; for (i = 0; i < T * T; i++) sd += (A0[i] - mAll) * (A0[i] - mAll); sd = Math.sqrt(sd / (T * T)) || 1e-6;
    var bestC = -Infinity, tx = 0, ty = 0;
    for (var oy = 0; oy < T; oy++) for (var ox = 0; ox < T; ox++) {
      var at = function (r, c) { return A0[((r + oy) % T) * T + ((c + ox) % T)] - mAll; };
      var sc = 0, cp = 0, c9;
      for (c9 = 0; c9 < T; c9++) sc += at(0, c9) * (WM_SYNC[c9] ? 1 : -1);
      for (var r9 = 1; r9 <= 3; r9++) for (c9 = 0; c9 < T; c9++) cp += at(r9, c9) * at(r9 + 4, c9);
      var score = sc / (T * sd) + cp / (24 * sd * sd);
      if (score > bestC) { bestC = score; tx = ox; ty = oy; }
    }
    var A = new Float32Array(T * T);
    for (i = 0; i < T; i++) for (j = 0; j < T; j++) A[j * T + i] = A0[((j + ty) % T) * T + ((i + tx) % T)];
    var one = 0, n1 = 0, zero = 0, n0 = 0;
    for (i = 0; i < T; i++) { if (WM_SYNC[i]) { one += A[i]; n1++; } else { zero += A[i]; n0++; } }
    one /= n1; zero /= n0;
    var spread = one - zero;
    if (spread <= 0.15) return { ok: false, reason: "sync row not found (spread " + spread.toFixed(3) + ")", pitch: p };
    var thr = (one + zero) / 2;
    var bits = [], margins = [];
    for (var b = 0; b < 32; b++) {
      var r1 = 1 + (b >> 3), c1 = b & 7, v1 = A[r1 * T + c1] - thr;
      var v = v1;
      if (b < 24) { var r2 = 5 + (b >> 3), v2 = A[r2 * T + c1] - thr; v = (Math.abs(v2) > Math.abs(v1)) ? v2 : v1; if ((v1 > 0) === (v2 > 0)) v = (v1 + v2) / 2; }
      bits.push(v > 0 ? 1 : 0); margins.push(Math.abs(v) / spread);
    }
    var code = 0; for (b = 0; b < 32; b++) code = ((code << 1) | bits[b]) >>> 0;
    var minMargin = Math.min.apply(null, margins);
    /* duplicate rows (5–7 repeat 1–3) must agree, and a zero payload is never valid */
    var agree = 0; for (b = 0; b < 24; b++) { var ra = A[(1 + (b >> 3)) * T + (b & 7)] - thr, rb = A[(5 + (b >> 3)) * T + (b & 7)] - thr; if ((ra > 0) === (rb > 0)) agree++; }
    var parsed = (agree >= 21 && ((code >>> 8) & 0xFFFFFF) !== 0) ? wmParseCode(code) : null;
    return { ok: !!parsed, code: code, hex: ("00000000" + code.toString(16)).slice(-8).toUpperCase(), kind: parsed ? parsed.kind : null, value: parsed ? parsed.value : null,
             pitch: (pX + pY) / 2, hp: hpR, tiles: Math.floor(cols / T) * Math.floor(rows / T), confidence: Math.max(0, Math.min(1, minMargin * 2)), spread: spread,
             agree: agree, reason: parsed ? null : (agree < 21 ? "duplicate rows disagree (" + agree + "/24)" : "checksum failed") };
  };
  var r1 = readAt(p);
  if (!r1.ok && p / 2 >= 3.5) { var r2 = readAt(p / 2); if (r2.ok || (r2.spread || 0) > (r1.spread || 0)) return r2; }
  return r1;
}
